import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useProject } from '../hooks/useProjects';
import { ArrowLeft, MapPin, Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';

export function ProjectDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data: project, isLoading } = useProject(id ? parseInt(id) : 0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-neutral-200 border-t-neutral-900 rounded-full"
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl text-neutral-400 tracking-wide mb-6">{t('projects.notFound')}</p>
          <Link to="/projects">
            <button className="px-8 py-3 border border-neutral-300 hover:bg-neutral-50 tracking-[0.2em] uppercase text-sm">
              {t('projects.backToProjects')}
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const images = project.images || [];
  const currentImage = images[selectedImageIndex];

  const goToPrev = () => setSelectedImageIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  const goToNext = () => setSelectedImageIndex((i) => (i < images.length - 1 ? i + 1 : 0));

  return (
    <div className="min-h-screen pt-32 pb-16">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-16">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 mb-12 text-neutral-600 hover:text-neutral-900 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="tracking-wide text-sm uppercase">{t('projects.backToProjects')}</span>
          </Link>
        </motion.div>

        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          {project.category && (
            <span className="text-xs tracking-[0.3em] uppercase text-neutral-500 mb-4 block">
              {t(`projects.categories.${project.category}`)}
            </span>
          )}
          <h1 className="text-5xl md:text-6xl tracking-[0.1em] uppercase font-light mb-6">
            {project.title}
          </h1>
          <div className="flex items-center gap-6 text-neutral-500">
            {project.location && (
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {project.location}
              </span>
            )}
            {project.year && (
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {project.year}
              </span>
            )}
          </div>
        </motion.div>

        {/* Main Image */}
        {images.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-6"
          >
            <div
              className="aspect-[16/9] overflow-hidden bg-neutral-100 relative cursor-pointer group"
              onClick={() => setLightboxOpen(true)}
            >
              <img
                src={currentImage?.imageUrl}
                alt={`${project.title} - ${selectedImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToNext(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
              <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/50 text-white text-sm backdrop-blur-sm">
                {selectedImageIndex + 1} / {images.length}
              </div>
            </div>
          </motion.div>
        )}

        {/* Thumbnails */}
        {images.length > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex gap-3 mb-12 overflow-x-auto pb-2"
          >
            {images.map((img, index) => (
              <button
                key={img.id}
                onClick={() => setSelectedImageIndex(index)}
                className={`flex-shrink-0 w-24 h-24 overflow-hidden border-2 transition-all duration-300 ${
                  index === selectedImageIndex
                    ? 'border-neutral-900 opacity-100'
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={img.imageUrl}
                  alt={`${project.title} thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </motion.div>
        )}

        {/* Description */}
        {project.description && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-3xl mb-16"
          >
            <h2 className="text-2xl tracking-[0.15em] uppercase font-light mb-6">
              {t('projects.aboutProject')}
            </h2>
            <p className="text-lg text-neutral-700 leading-relaxed tracking-wide">
              {project.description}
            </p>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="pt-8 border-t border-neutral-200"
        >
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-12 py-5 bg-neutral-900 text-white hover:bg-neutral-800 tracking-[0.2em] uppercase text-sm transition-colors duration-300 shadow-lg shadow-black/10"
            >
              {t('projects.startProject')}
            </motion.button>
          </Link>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && currentImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            <img
              src={currentImage.imageUrl}
              alt={project.title}
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
